import { defineStore } from 'pinia'
import { reactive } from 'vue'

interface RegistryEntry {
  key: string
  value: any
  readOnly?: boolean
}

interface BackendApi {
  fetch(url: string, init?: RequestInit): Promise<any>
}

interface ErrorHandler {
  error(spec: { code?: string; title?: string; message: string }): void
}

const NOT_FOUND = Symbol('NOT_FOUND')

export function createRegistryStore(backend: BackendApi, procs?: ErrorHandler) {
  return defineStore('registry', () => {
    const backendFetch = backend.fetch.bind(backend)

    const entries = reactive(new Map<string, any>())
    const lists = reactive(new Map<string, Record<string, any>>())
    const pendingGets = new Map<string, Promise<any>>()

    function handleError(spec: { code?: string; title?: string; message: string }) {
      procs?.error(spec)
    }

    async function get(key: string): Promise<any> {
      if (entries.has(key)) {
        const cached = entries.get(key)
        return cached === NOT_FOUND ? undefined : cached
      }

      if (pendingGets.has(key)) {
        return pendingGets.get(key)
      }

      const promise = fetchGet(key)
      pendingGets.set(key, promise)

      try {
        return await promise
      } finally {
        pendingGets.delete(key)
      }
    }

    async function fetchGet(key: string): Promise<any> {
      const response = await backendFetch(`/core/resources/registry/${key}?searchType=exact`)

      if (!response.ok) {
        const errors = await response.errors()
        if (errors.length === 1 && errors[0].code === 'NOT_FOUND') {
          entries.set(key, NOT_FOUND)
          return undefined
        }
        errors.forEach((e: any) => handleError(e))
        throw errors
      }

      const entry = await response.json() as RegistryEntry
      entries.set(key, entry.value)
      return entry.value
    }

    async function list(path: string | undefined, immediate?: boolean): Promise<Record<string, any>> {
      const cacheKey = `${path ?? ''}|${immediate ?? false}`

      if (lists.has(cacheKey)) {
        return lists.get(cacheKey)!
      }

      let searchPath = (path ?? '').replace(/^\/+/, '').replace(/\/+$/, '')
      if (searchPath) {
        searchPath = `/${searchPath}/`
      }

      const response = await backendFetch(`/core/resources/registry${searchPath}?searchType=prefix`)

      if (!response.ok) {
        const errors = await response.errors()
        errors.forEach((e: any) => handleError(e))
        throw errors
      }

      const result = await response.json() as RegistryEntry[]

      for (const entry of result) {
        entries.set(entry.key, entry.value)
      }

      let output: Record<string, any>

      if (immediate) {
        const expectedLevels = searchPath.split('/').length - 1
        output = {}
        for (const entry of result) {
          const levels = entry.key.split('/').length
          if (levels === expectedLevels) {
            output[entry.key] = entry.value
          }
        }
      } else {
        output = result.reduce((res, entry) => {
          res[entry.key] = entry.value
          return res
        }, {} as Record<string, any>)
      }

      lists.set(cacheKey, output)
      return output
    }

    async function set(key: string, value: any): Promise<void> {
      const response = await backendFetch(`/core/resources/registry/${key}`, {
        method: 'POST',
        body: JSON.stringify(value)
      })

      if (!response.ok) {
        const errors = await response.errors()
        errors.forEach((e: any) => handleError(e))
        throw errors
      }

      entries.set(key, value)
      lists.clear()
    }

    async function clear(key: string): Promise<void> {
      const response = await backendFetch(`/core/resources/registry/${key}`, {
        method: 'DELETE'
      })

      if (!response.ok) {
        const errors = await response.errors()
        errors.forEach((e: any) => handleError(e))
        throw errors
      }

      entries.set(key, NOT_FOUND)
      lists.clear()
    }

    async function index(path: string): Promise<string[]> {
      const response = await backendFetch(`/core/resources/registry/${path}?searchType=prefix&index`)

      if (!response.ok) {
        const errors = await response.errors()
        errors.forEach((e: any) => handleError(e))
        throw errors
      }

      return await response.json() as string[]
    }

    function invalidate(key?: string) {
      if (key) {
        entries.delete(key)
        lists.clear()
      } else {
        entries.clear()
        lists.clear()
      }
    }

    return {
      entries,
      lists,
      get,
      list,
      set,
      clear,
      index,
      invalidate,
    }
  })()
}
